import React, { useEffect, useMemo, useState } from 'react';
import apiClient from '../../assets/scripts/services/apiClient';
import authService from '../../assets/scripts/auth/authService';
import JsonEditor from './JsonEditor';

const ENV_OPTIONS = ['development', 'staging', 'production'];

const CredentialManagement = () => {
  // List state
  const [credentials, setCredentials] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState('');
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loadingCredential, setLoadingCredential] = useState(false);
  
  // Form data
  const [organizations, setOrganizations] = useState([]);
  const [applications, setApplications] = useState([]);
  const [templates, setTemplates] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [loadingApps, setLoadingApps] = useState(false);
  const [error, setError] = useState('');
  
  const [orgId, setOrgId] = useState('');
  const [appId, setAppId] = useState('');
  const [env, setEnv] = useState('development');
  const [integrationName, setIntegrationName] = useState('');
  const [useTemplate, setUseTemplate] = useState(true);
  const [templateId, setTemplateId] = useState('');
  const [jsonValid, setJsonValid] = useState(true);
  const [jsonValue, setJsonValue] = useState({});
  
  // Track original values for change detection
  const [originalJsonValue, setOriginalJsonValue] = useState({});
  const [originalEnv, setOriginalEnv] = useState('');
  const [originalIntegrationName, setOriginalIntegrationName] = useState('');

  // Load credentials list
  const loadCredentials = async () => {
    try {
      setLoadingList(true);
      setListError('');
      const data = await apiClient.get('/integration-credentials');
      setCredentials(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load credentials:', e);
      setListError('Failed to load credentials');
    } finally {
      setLoadingList(false);
    }
  };

  // Initial load - credentials list, organizations and templates
  useEffect(() => {
    const run = async () => {
      const ok = await authService.requireAuth();
      if (!ok) return;
      
      // Load credentials list
      loadCredentials();
      
      // Load form data
      try {
        setLoading(true);
        const [orgs, tpls] = await Promise.all([
          apiClient.get('/organizations'),
          apiClient.get('/integration-templates'),
        ]);
        setOrganizations(Array.isArray(orgs) ? orgs : []);
        setTemplates(Array.isArray(tpls) ? tpls : []);
      } catch (e) {
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  // Load applications when organization is selected
  useEffect(() => {
    if (!orgId) {
      setApplications([]);
      setAppId(''); // Clear selected app when org changes
      return;
    }

    const loadOrgApplications = async () => {
      try {
        setLoadingApps(true);
        setError('');
        // Use the organization-specific applications endpoint
        const response = await apiClient.get(`/organizations/${orgId}/applications`);
        
        // Extract applications from the response structure
        const apps = response?.applications || [];
        setApplications(Array.isArray(apps) ? apps : []);
        setAppId(''); // Reset app selection when org changes
      } catch (e) {
        console.error('Failed to load organization applications:', e);
        setApplications([]);
        // Don't show error for 404 - organization might not have apps
        if (e.response?.status !== 404) {
          setError('Failed to load applications for this organization');
        }
      } finally {
        setLoadingApps(false);
      }
    };

    loadOrgApplications();
  }, [orgId]);

  // Handle template selection
  useEffect(() => {
    // Skip this effect if we're editing - the JSON value is already set from the API
    if (editing) {
      return;
    }
    
    if (useTemplate && templateId) {
      const t = templates.find((x) => String(x.id) === String(templateId));
      if (t && t.template_data) {
        // Create a copy of the template data, excluding the 'name' field
        const templateCopy = { ...t.template_data };
        delete templateCopy.name; // Remove the template name as it's not needed in credentials
        
        // If the template has a specific structure, use it
        // Otherwise, if it only has fields array, create empty fields
        if (Object.keys(templateCopy).length > 0) {
          // Check if it's just a fields array or a full structure
          if (templateCopy.fields && Array.isArray(templateCopy.fields) && Object.keys(templateCopy).length === 1) {
            // It's just a fields array, create empty fields
            const seeded = {};
            templateCopy.fields.forEach((f) => {
              if (typeof f === 'string') seeded[f] = '';
            });
            setJsonValue(Object.keys(seeded).length ? seeded : {});
          } else {
            // It's a full template structure, use it as the starting point
            setJsonValue(templateCopy);
          }
        } else {
          setJsonValue({});
        }
      } else if (!useTemplate || !templateId) {
        // Clear the JSON when no template is selected or template mode is disabled
        setJsonValue({});
      }
    } else if (!useTemplate) {
      // Clear when switching from template to manual mode (only for new credentials)
      setJsonValue({});
    }
  }, [useTemplate, templateId, templates, editing]);

  // Calculate if we can submit - using useMemo with all dependencies
  const canSubmit = useMemo(() => {
    // First check if JSON is valid
    if (!jsonValid) {
      console.log('Cannot submit: JSON is invalid');
      return false;
    }
    
    // Check required fields
    const hasRequiredFields = orgId && appId && env && integrationName.trim();
    
    if (editing) {
      // For editing, we need required fields AND at least one change
      if (!hasRequiredFields) {
        console.log('Cannot submit: Missing required fields');
        return false;
      }
      
      // Simple JSON comparison using stringify (handles arrays and nested objects)
      const jsonStr1 = JSON.stringify(jsonValue);
      const jsonStr2 = JSON.stringify(originalJsonValue);
      const jsonChanged = jsonStr1 !== jsonStr2;
      
      const envChanged = env !== originalEnv;
      const nameChanged = integrationName.trim() !== originalIntegrationName.trim();
      
      const result = jsonChanged || envChanged || nameChanged;
      
      // Debug logging
      console.log('Edit mode change detection:', {
        jsonChanged,
        envChanged,
        nameChanged,
        canSubmit: result,
        jsonValue,
        originalJsonValue,
        jsonStr1: jsonStr1.substring(0, 100),
        jsonStr2: jsonStr2.substring(0, 100),
        env,
        originalEnv,
        integrationName: integrationName.trim(),
        originalIntegrationName: originalIntegrationName.trim()
      });
      
      return result;
    } else {
      // For new credentials, just need required fields
      return hasRequiredFields;
    }
  }, [jsonValid, orgId, appId, env, integrationName, editing, jsonValue, originalJsonValue, originalEnv, originalIntegrationName]);

  const openCreateModal = () => {
    setEditing(null);
    setOrgId('');
    setAppId('');
    setEnv('development');
    setIntegrationName('');
    setUseTemplate(true);
    setTemplateId('');
    setJsonValue({});
    setJsonValid(true);
    
    // Reset original values
    setOriginalJsonValue({});
    setOriginalEnv('');
    setOriginalIntegrationName('');
    
    setModalOpen(true);
  };

  const openEditModal = async (credential) => {
    setLoadingCredential(true);
    setModalOpen(true); // Open modal immediately to show loading state
    
    try {
      // Fetch full credential details
      const fullCredential = await apiClient.get(`/integration-credentials/${credential.id}`);
      
      setEditing(fullCredential);
      setOrgId(String(fullCredential.organization_id || ''));
      
      // Load applications for this organization first
      if (fullCredential.organization_id) {
        setLoadingApps(true);
        try {
          const response = await apiClient.get(`/organizations/${fullCredential.organization_id}/applications`);
          const apps = response?.applications || [];
          setApplications(Array.isArray(apps) ? apps : []);
        } catch (e) {
          console.error('Failed to load organization applications:', e);
          setApplications([]);
        } finally {
          setLoadingApps(false);
        }
      }
      
      setAppId(String(fullCredential.app_registry_id || ''));
      setEnv(fullCredential.environment || 'development');
      setIntegrationName(fullCredential.integration_application_name || '');
      
      // Set template state based on whether credential has a template_id
      const hasTemplate = !!fullCredential.template_id;
      setUseTemplate(hasTemplate);
      setTemplateId(fullCredential.template_id ? String(fullCredential.template_id) : '');
      
      // Always set the JSON value from the credential
      const credentialData = fullCredential.credential || {};
      setJsonValue(credentialData);
      setJsonValid(true);
      
      // Store original values for change detection - create deep copy to avoid reference issues
      // Use a stable stringification to ensure consistent comparison
      const originalData = JSON.parse(JSON.stringify(credentialData));
      setOriginalJsonValue(originalData);
      setOriginalEnv(fullCredential.environment || 'development');
      setOriginalIntegrationName((fullCredential.integration_application_name || '').trim());
      
      console.log('Loaded credential for editing:', {
        credentialData,
        originalData,
        env: fullCredential.environment || 'development',
        name: fullCredential.integration_application_name || ''
      });
    } catch (e) {
      console.error('Failed to fetch credential details:', e);
      alert('Failed to load credential details');
      setModalOpen(false); // Close modal on error
    } finally {
      setLoadingCredential(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setError('');
  };

  const saveCredential = async () => {
    if (!canSubmit) {
      alert('Please complete all required fields with valid JSON.');
      return;
    }
    const payload = {
      app_registry_id: parseInt(appId),
      organization_id: parseInt(orgId),
      integration_application_name: integrationName.trim(),
      credential: jsonValue || {},
      environment: env,
      ...(useTemplate && templateId ? { template_id: parseInt(templateId) } : {}),
    };
    try {
      if (editing) {
        await apiClient.put(`/integration-credentials/${editing.id}`, payload);
        alert('Credential updated');
      } else {
        await apiClient.post('/integration-credentials', payload);
        alert('Credential saved');
      }
      closeModal();
      await loadCredentials();
    } catch (e) {
      alert('Failed to save credential');
    }
  };

  const deleteCredential = async (id) => {
    if (!confirm('Are you sure you want to delete this credential?')) return;
    try {
      await apiClient.delete(`/integration-credentials/${id}`);
      alert('Credential deleted');
      await loadCredentials();
    } catch (e) {
      alert('Failed to delete credential');
    }
  };

  const orgOptions = useMemo(() => organizations.map((o) => ({ id: o.id, name: o.detail?.org_name || `Organization ${o.id}` })), [organizations]);
  const appOptions = useMemo(() => applications.map((a) => ({ id: a.id, name: a.name || `App ${a.id}` })), [applications]);
  const templateOptions = useMemo(() => templates.map((t) => ({ id: t.id, name: t.template_data?.name || `Template ${t.id}` })), [templates]);

  // Find organization and application names for display
  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.detail?.org_name || `Org ${orgId}`;
  };

  const getAppName = (appId) => {
    // First check loaded applications
    const app = applications.find(a => a.id === appId);
    if (app) return app.name || `App ${appId}`;
    
    // If not found, check all credentials for app names (they might have apps from different orgs)
    return `App ${appId}`;
  };

  if (loadingList) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (listError) {
    return (
      <div className="alert alert-danger" role="alert">
        {listError}
      </div>
    );
  }

  return (
    <div className="credential-management">
      {/* Header with Add button */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Integration Credentials</h5>
        <button className="btn btn-primary" onClick={openCreateModal}>
          <i className="ti-plus me-2"></i>Add Credential
        </button>
      </div>

      {/* Credentials Table */}
      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              <th>Organization</th>
              <th>Application</th>
              <th>Integration</th>
              <th>Environment</th>
              <th>Created</th>
              <th>Updated</th>
              <th width="150">Actions</th>
            </tr>
          </thead>
          <tbody>
            {credentials.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center text-muted py-4">
                  No credentials found. Click "Add Credential" to create one.
                </td>
              </tr>
            ) : (
              credentials.map((cred) => (
                <tr key={cred.id}>
                  <td>{getOrgName(cred.organization_id)}</td>
                  <td>{getAppName(cred.app_registry_id)}</td>
                  <td>
                    <span className="badge bg-secondary">
                      {cred.integration_application_name}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${
                      cred.environment === 'production' ? 'bg-danger' :
                      cred.environment === 'staging' ? 'bg-warning' :
                      'bg-info'
                    }`}>
                      {cred.environment}
                    </span>
                  </td>
                  <td>{new Date(cred.create_date).toLocaleDateString()}</td>
                  <td>{new Date(cred.update_date).toLocaleDateString()}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-1"
                      onClick={() => openEditModal(cred)}
                      title="Edit"
                    >
                      <i className="ti-pencil"></i>
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deleteCredential(cred.id)}
                      title="Delete"
                    >
                      <i className="ti-trash"></i>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal for Create/Edit */}
      {modalOpen && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editing ? 'Edit Credential' : 'Add Credential'}
                  </h5>
                  <button type="button" className="btn-close" onClick={closeModal}></button>
                </div>
                <div className="modal-body">
                  {loadingCredential ? (
                    <div className="text-center p-4">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading credential details...</span>
                      </div>
                      <p className="mt-2">Loading credential details...</p>
                    </div>
                  ) : (
                    <>
                      {error && (
                        <div className="alert alert-danger mb-3" role="alert">
                          {error}
                        </div>
                      )}
                      
                      <div className="alert alert-info mb-3">
                        <strong>Steps:</strong> 1. Select Organization → 2. Select Application → 3. Configure Credentials
                      </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Organization <span className="text-danger">*</span>
                        </label>
                        <select 
                          className="form-control" 
                          value={orgId} 
                          onChange={(e) => setOrgId(e.target.value)}
                          disabled={editing}
                        >
                          <option value="">Select organization (required)</option>
                          {orgOptions.map((o) => (
                            <option key={o.id} value={o.id}>{o.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Application
                          {!orgId && <span className="text-muted ms-2">(Select organization first)</span>}
                          {loadingApps && <span className="text-muted ms-2">(Loading...)</span>}
                        </label>
                        <select 
                          className="form-control" 
                          value={appId} 
                          onChange={(e) => setAppId(e.target.value)}
                          disabled={!orgId || loadingApps || editing}
                        >
                          <option value="">
                            {!orgId ? 'Select organization first' : 
                             loadingApps ? 'Loading applications...' :
                             appOptions.length === 0 ? 'No applications available' :
                             'Select application'}
                          </option>
                          {appOptions.map((a) => (
                            <option key={a.id} value={a.id}>{a.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Environment</label>
                        <select className="form-control" value={env} onChange={(e) => setEnv(e.target.value)}>
                          {ENV_OPTIONS.map((eopt) => (
                            <option key={eopt} value={eopt}>{eopt}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-8">
                      <div className="mb-3">
                        <label className="form-label">Integration Application Name</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g., slack, github, aws"
                          value={integrationName}
                          onChange={(e) => setIntegrationName(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mb-3 d-flex align-items-center">
                    <div className="form-check form-switch me-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="useTemplateSwitch"
                        checked={useTemplate}
                        onChange={() => setUseTemplate((v) => !v)}
                      />
                      <label className="form-check-label" htmlFor="useTemplateSwitch">
                        Use Template
                      </label>
                    </div>
                    {useTemplate && (
                      <div style={{ minWidth: 260 }}>
                        <select className="form-control" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                          <option value="">Select template</option>
                          {templateOptions.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  <div className="mb-2">
                    <label className="form-label">
                      Credential JSON
                      {useTemplate && templateId && (
                        <span className="text-muted ms-2">
                          (Template populated - modify as needed)
                        </span>
                      )}
                    </label>
                    <JsonEditor
                      value={jsonValue}
                      onChange={(valid, val) => {
                        console.log('JsonEditor onChange:', { valid, val });
                        setJsonValid(valid);
                        if (valid && val !== null) {
                          setJsonValue(val);
                        }
                      }}
                      height={300}
                    />
                  </div>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  {editing && !canSubmit && !loadingCredential && (
                    <span className="text-muted me-auto">
                      <i className="ti-info-alt me-1"></i>
                      No changes detected
                    </span>
                  )}
                  <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={loadingCredential}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={saveCredential} disabled={!canSubmit || loadingCredential}>
                    {editing ? 'Update' : 'Save'} Credential
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default CredentialManagement;